import {
  Avatar,
  MentionsText,
  OptionStatusIndicator,
  OptionType,
  Separator,
} from 'platform/components';
import {Box, HStack, Show, Space, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useEffect, useRef, useState} from 'react';

import {isNotNil, reject} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {CommentResponseBody} from '@dms/api/core';
import {
  useGetCurrentUserInfoQuery,
  useGetUserAvatarUrlQuery,
  useGetUserQuery,
} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {parseDate, suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import {queryParams} from '../../../utils/queryParams';
import {CommentCardItemActions} from './CommentCardItemActions';
import {CommentsCardInput, type CommentCardForm} from './CommentsCardInput';

export interface CommentCardItemProps extends TestIdProps {
  comment: CommentResponseBody;
  userOptions?: OptionType[];
  isDeleteCommentLoading?: boolean;
  isEditCommentLoading?: boolean;
  isDeleteDisabled?: boolean;
  isEditDisabled?: boolean;
  onEdit?: (id: string, text: string) => Promise<void>;
  onDelete?: (id: string) => void;
}

export function CommentCardItem(props: CommentCardItemProps) {
  const dateTimeFormatter = useDateTimeFormatter();
  const [commentIdParam] = useQueryState(queryParams.COMMENT_ID);
  const ref = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);

  const {data: user} = useGetUserQuery({id: props.comment.createdBy});
  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {data: avatarUrl} = useGetUserAvatarUrlQuery({
    userId: props.comment.createdBy,
    width: 120,
    height: 120,
  });

  const canEdit = currentUser?.id === props.comment.createdBy;
  const canDelete = currentUser?.id === props.comment.createdBy;

  const isHighlighted = props.comment.id === commentIdParam;

  const statusIndicatorById = new Map<string, OptionStatusIndicator>();
  props.userOptions?.forEach((option) => {
    if (isNotNil(option.statusIndicator)) {
      statusIndicatorById.set(option.value, option.statusIndicator);
    }
  });

  useEffect(
    function scrollIntoViewWhenHighlighted() {
      if (isHighlighted) {
        ref.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
      }
    },
    [isHighlighted]
  );

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = (values: CommentCardForm): Promise<void> => {
    const trimmed = values.comment?.trim() ?? '';
    if (isNotNil(trimmed) && trimmed !== props.comment.text && isNotNil(props.onEdit)) {
      return props.onEdit(props.comment.id, trimmed).finally(() => {
        setIsEditing(false);
      });
    }
    return Promise.resolve().finally(() => {
      setIsEditing(false);
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const showActions = (props.onDelete && canDelete) || (props.onEdit && canEdit);
  const authorName = user && reject(isNilOrEmpty, [user.firstName, user.lastName]).join(' ');
  const createdAt = `${i18n.t('page.comments.labels.created', {
    created: dateTimeFormatter('dateTimeShort', parseDate(props.comment.createdAt)),
  })}`;
  const updatedAt = isNotNil(props.comment.updatedAt)
    ? i18n.t('page.comments.labels.edited', {
        edited: dateTimeFormatter('dateTimeShort', parseDate(props.comment.updatedAt)),
      })
    : null;

  if (isEditing) {
    return (
      <Box
        borderRadius="small"
        backgroundColor={isHighlighted ? 'palettes.neutral.20.100' : undefined}
        padding={3}
        ref={ref}
      >
        <CommentsCardInput
          key={props.comment.id}
          defaultComment={props.comment.text}
          isSending={props.isEditCommentLoading}
          onCancel={handleCancelEdit}
          onSubmit={handleSaveEdit}
          userOptions={props.userOptions}
          data-testid={suffixTestId('editForm', props)}
        />
      </Box>
    );
  }

  return (
    <Box
      borderRadius="small"
      backgroundColor={isHighlighted ? 'palettes.neutral.20.100' : undefined}
      padding={3}
      ref={ref}
    >
      <HStack spacing={3} align="flex-start">
        <Avatar
          name={authorName}
          imageUrl={avatarUrl}
          data-testid={suffixTestId('authorName', props)}
        />
        <Box flex={1}>
          <Text size="small" alternative data-testid={suffixTestId('authorName', props)}>
            {authorName}
          </Text>
          <HStack>
            <Text size="xSmall" color="tertiary" data-testid={suffixTestId('createdAt', props)}>
              {createdAt}
            </Text>
            <Show when={isNotNil(updatedAt)}>
              <Separator orientation="vertical" spacing={2} />
              <Text size="xSmall" color="tertiary" data-testid={suffixTestId('updatedAt', props)}>
                {updatedAt}
              </Text>
            </Show>
          </HStack>
          <Space vertical={2} />
          <MentionsText
            value={props.comment.text}
            statusIndicatorById={statusIndicatorById}
            data-testid={suffixTestId('text', props)}
          />
        </Box>
        <Show when={showActions}>
          <CommentCardItemActions
            id={props.comment.id}
            isDeleteCommentLoading={props.isDeleteCommentLoading}
            isEditCommentLoading={props.isEditCommentLoading}
            onDelete={props.onDelete}
            onEditClick={props.onEdit ? handleEditClick : undefined}
            isDeleteDisabled={props.isDeleteDisabled}
            isEditDisabled={props.isEditDisabled}
            data-testid={suffixTestId('actions', props)}
          />
        </Show>
      </HStack>
    </Box>
  );
}
